/**
 * 测量工具：基于 SVG 叠加层在场景中点击拾取世界坐标，
 * 显示折线、各线段长度和点位字母（A/B/C…），并使用 TranslateGizmo 拖拽编辑选中点。
 *
 * 参考 manifold_supersplat 的 measure-tool：
 * - 单击空白：拾取并新增测量点，同时选中新点
 * - 单击已有测量点：选中该点（高亮 + 显示三轴 gizmo）
 * - 拖拽 gizmo：实时更新选中点的世界坐标
 * - Delete / Backspace：删除当前选中点
 * - Esc：清空全部测量点
 */

import { Entity, EventHandler, Gizmo, TranslateGizmo, type AppBase, type Layer, Vec3 } from 'playcanvas';

import type { Picker } from './picker';

const SVGNS = 'http://www.w3.org/2000/svg';

// 点位标记：A-Z / AA-ZZ
const getPointLabel = (index: number) => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let label = '';
    let i = index;
    while (i >= 0) {
        label = alphabet[i % alphabet.length] + label;
        i = Math.floor(i / alphabet.length) - 1;
    }
    return label;
};

const tmp = new Vec3();
const tmp0 = new Vec3();
const tmp1 = new Vec3();

const FEET_PER_METER = 3.280839895;

function formatLength(meters: number, unitSystem: 'metric' | 'imperial'): string {
    if (unitSystem === 'imperial') {
        return `${(meters * FEET_PER_METER).toFixed(2)} ft`;
    }
    return `${meters.toFixed(2)} m`;
}

class MeasureTool {
    private app: AppBase;

    private camera: Entity;

    private canvas: HTMLCanvasElement;

    private picker: Picker;

    private svg: SVGSVGElement;

    private lineBottom: SVGPolylineElement;

    private lineTop: SVGPolylineElement;

    private pointsGroup: SVGGElement;

    private labelsGroup: SVGGElement;

    private distancesGroup: SVGGElement;

    private pointEls: SVGCircleElement[] = [];

    private labelEls: SVGTextElement[] = [];

    private distanceEls: SVGTextElement[] = [];

    private points: Vec3[] = [];

    /** 当前选中点的索引，-1 表示未选中 */
    private selection = -1;

    /** TranslateGizmo 及其绑定的占位 entity（懒加载，激活后再创建） */
    private gizmo: TranslateGizmo | null = null;

    private gizmoLayer: Layer | null = null;

    private gizmoPivot: Entity | null = null;

    /** gizmo 正在拖拽中，pointerup 不再当作单击处理 */
    private gizmoBusy = false;

    private active = false;

    private unitSystem: 'metric' | 'imperial' = 'metric';

    private clicked = false;

    private downX = 0;

    private downY = 0;

    private onPointerDown = (e: PointerEvent) => {
        if (this.isPrimary(e)) {
            this.clicked = true;
            this.downX = e.clientX;
            this.downY = e.clientY;
        }
    };

    private onPointerMove = (e: PointerEvent) => {
        // 拖动相机时不应触发添加点；容差 6px 以兼容触摸抖动
        if (this.clicked &&
            (Math.abs(e.clientX - this.downX) + Math.abs(e.clientY - this.downY)) > 6) {
            this.clicked = false;
        }
    };

    private onPointerUp = (e: PointerEvent) => {
        // 如果 gizmo 正在拖拽，pointerup 由 gizmo 消费，不要当作单击处理
        if (this.gizmoBusy) {
            this.clicked = false;
            return;
        }
        if (!this.clicked || !this.isPrimary(e)) return;
        this.clicked = false;

        // 注意：这里不能 stopPropagation —— 吞掉 pointerup 会让
        // MultiTouchSource 的触摸计数错乱（移动端会卡成"多指"状态）。
        // 与相机点击导航的冲突由 nav-interaction 侧监听 measure.active 跳过。

        const offsetX = e.offsetX;
        const offsetY = e.offsetY;

        // 优先匹配是否点击到已有点位（屏幕距离 < 12px）→ 选中该点
        const hitIdx = this.hitTest(offsetX, offsetY);
        if (hitIdx >= 0) {
            this.setSelection(hitIdx);
            return;
        }

        // 否则做世界拾取，将命中的世界坐标加入测量点（异步），并选中新点
        const w = this.canvas.clientWidth;
        const h = this.canvas.clientHeight;
        if (w <= 0 || h <= 0) return;
        this.picker.pick(offsetX / w, offsetY / h).then((pos) => {
            if (!pos || !this.active) return;
            this.points.push(pos.clone());
            this.setSelection(this.points.length - 1);
        });
    };

    private onKeyDown = (e: KeyboardEvent) => {
        if (!this.active) return;
        if (e.key === 'Escape') {
            this.clear();
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
            this.deleteSelected();
        }
    };

    private onPostrender = () => {
        if (this.active) this.render();
    };

    private events: EventHandler | null;

    constructor(app: AppBase, camera: Entity, picker: Picker, events?: EventHandler) {
        this.app = app;
        this.camera = camera;
        this.picker = picker;
        this.events = events ?? null;
        this.canvas = app.graphicsDevice.canvas as HTMLCanvasElement;

        // 建立 SVG 叠加层：覆盖整个 canvas，pointer-events 默认无效，
        // 由 canvas 自身处理 pointer 事件。
        const svg = document.createElementNS(SVGNS, 'svg');
        svg.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;overflow:visible;z-index:2;display:none';
        svg.classList.add('measure-tool-svg');

        const lineBottom = document.createElementNS(SVGNS, 'polyline');
        lineBottom.setAttribute('class', 'measure-line-bottom');
        const lineTop = document.createElementNS(SVGNS, 'polyline');
        lineTop.setAttribute('class', 'measure-line-top');

        const pointsGroup = document.createElementNS(SVGNS, 'g');
        const labelsGroup = document.createElementNS(SVGNS, 'g');
        const distancesGroup = document.createElementNS(SVGNS, 'g');

        svg.appendChild(lineBottom);
        svg.appendChild(lineTop);
        svg.appendChild(pointsGroup);
        svg.appendChild(labelsGroup);
        svg.appendChild(distancesGroup);

        this.canvas.parentElement!.appendChild(svg);

        this.svg = svg;
        this.lineBottom = lineBottom;
        this.lineTop = lineTop;
        this.pointsGroup = pointsGroup;
        this.labelsGroup = labelsGroup;
        this.distancesGroup = distancesGroup;

        // 渲染回调：每帧根据相机更新点位/线段位置
        app.on('postrender', this.onPostrender);
    }

    private isPrimary(e: PointerEvent) {
        return e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary;
    }

    // 将世界坐标投影到 CSS 像素坐标
    private worldToScreen(p: Vec3, out: Vec3) {
        this.camera.camera.worldToScreen(p, out);
        return out;
    }

    // 检查屏幕坐标 (sx, sy) 是否命中已有测量点（返回索引或 -1）
    private hitTest(sx: number, sy: number) {
        for (let i = 0; i < this.points.length; i++) {
            this.worldToScreen(this.points[i], tmp);
            if (Math.abs(tmp.x - sx) < 12 && Math.abs(tmp.y - sy) < 12) {
                return i;
            }
        }
        return -1;
    }

    private ensureElements(count: number) {
        while (this.pointEls.length < count) {
            const c = document.createElementNS(SVGNS, 'circle');
            c.setAttribute('class', 'measure-point');
            c.setAttribute('r', '5');
            this.pointsGroup.appendChild(c);
            this.pointEls.push(c);
        }
        while (this.labelEls.length < count) {
            const t = document.createElementNS(SVGNS, 'text');
            t.setAttribute('class', 'measure-point-label');
            t.setAttribute('text-anchor', 'middle');
            this.labelsGroup.appendChild(t);
            this.labelEls.push(t);
        }
        const segCount = Math.max(0, count - 1);
        while (this.distanceEls.length < segCount) {
            const t = document.createElementNS(SVGNS, 'text');
            t.setAttribute('class', 'measure-segment-label');
            t.setAttribute('text-anchor', 'middle');
            t.setAttribute('dy', '-8');
            this.distancesGroup.appendChild(t);
            this.distanceEls.push(t);
        }
    }

    private render() {
        const count = this.points.length;
        this.ensureElements(count);

        if (count === 0) {
            this.lineBottom.setAttribute('visibility', 'hidden');
            this.lineTop.setAttribute('visibility', 'hidden');
            this.pointEls.forEach(el => el.setAttribute('visibility', 'hidden'));
            this.labelEls.forEach(el => el.setAttribute('visibility', 'hidden'));
            this.distanceEls.forEach(el => el.setAttribute('visibility', 'hidden'));
            return;
        }

        // 点位与折线
        let polyAttr = '';
        for (let i = 0; i < count; i++) {
            this.worldToScreen(this.points[i], tmp);
            const x = tmp.x;
            const y = tmp.y;
            polyAttr += `${x},${y} `;

            const c = this.pointEls[i];
            c.setAttribute('cx', x.toString());
            c.setAttribute('cy', y.toString());
            c.setAttribute('visibility', 'visible');
            // 高亮选中点
            c.classList.toggle('selected', i === this.selection);

            const lbl = this.labelEls[i];
            lbl.textContent = getPointLabel(i);
            lbl.setAttribute('x', x.toString());
            lbl.setAttribute('y', (y - 14).toString());
            lbl.setAttribute('visibility', 'visible');
        }
        for (let i = count; i < this.pointEls.length; i++) {
            this.pointEls[i].setAttribute('visibility', 'hidden');
        }
        for (let i = count; i < this.labelEls.length; i++) {
            this.labelEls[i].setAttribute('visibility', 'hidden');
        }

        const showLine = count > 1;
        this.lineBottom.setAttribute('visibility', showLine ? 'visible' : 'hidden');
        this.lineTop.setAttribute('visibility', showLine ? 'visible' : 'hidden');
        if (showLine) {
            const trimmed = polyAttr.trim();
            this.lineBottom.setAttribute('points', trimmed);
            this.lineTop.setAttribute('points', trimmed);
        }

        // 线段长度标签：显示在每条线段中点，并旋转与线段对齐
        const segCount = count - 1;
        for (let i = 0; i < segCount; i++) {
            this.worldToScreen(this.points[i], tmp0);
            this.worldToScreen(this.points[i + 1], tmp1);
            const sx0 = tmp0.x, sy0 = tmp0.y;
            const sx1 = tmp1.x, sy1 = tmp1.y;
            const mx = (sx0 + sx1) / 2;
            const my = (sy0 + sy1) / 2;

            const segDist = this.points[i].distance(this.points[i + 1]);

            const elem = this.distanceEls[i];
            elem.textContent = formatLength(segDist, this.unitSystem);
            elem.setAttribute('x', mx.toString());
            elem.setAttribute('y', my.toString());

            const angle = Math.atan2(sy1 - sy0, sx1 - sx0) * 180 / Math.PI;
            const dispAngle = angle > 90 ? angle - 180 : (angle < -90 ? angle + 180 : angle);
            elem.setAttribute('transform', `rotate(${dispAngle}, ${mx}, ${my})`);
            elem.setAttribute('visibility', 'visible');
        }
        for (let i = segCount; i < this.distanceEls.length; i++) {
            this.distanceEls[i].setAttribute('visibility', 'hidden');
        }
    }

    /** 激活测量工具：开启交互监听并显示 SVG 叠加层 */
    activate() {
        if (this.active) return;
        this.active = true;
        this.svg.style.display = '';

        // 鼠标变十字光标：通过 body class + CSS !important 实现，
        // 避免 nav-interaction 通过 inline style 改 canvas.cursor 时把它覆盖掉
        document.body.classList.add('measure-active');

        // 懒初始化 gizmo（首次激活时创建）
        this.ensureGizmo();

        // capture 阶段：先于相机控制器/NavCursor 拿到事件，方便单击命中后吞掉
        this.canvas.addEventListener('pointerdown', this.onPointerDown, true);
        this.canvas.addEventListener('pointermove', this.onPointerMove, true);
        this.canvas.addEventListener('pointerup', this.onPointerUp, true);
        window.addEventListener('keydown', this.onKeyDown);

        // 恢复之前的选中状态（若有）
        this.attachGizmoToSelection();
        this.app.renderNextFrame = true;
    }

    /** 关闭测量工具：移除监听器、隐藏 SVG（保留点位数据） */
    deactivate() {
        if (!this.active) return;
        this.active = false;
        this.svg.style.display = 'none';
        document.body.classList.remove('measure-active');
        this.canvas.removeEventListener('pointerdown', this.onPointerDown, true);
        this.canvas.removeEventListener('pointermove', this.onPointerMove, true);
        this.canvas.removeEventListener('pointerup', this.onPointerUp, true);
        window.removeEventListener('keydown', this.onKeyDown);

        // 暂时收起 gizmo（保留选中索引，下次激活时恢复）
        this.gizmo?.detach();
    }

    toggle() {
        if (this.active) this.deactivate();
        else this.activate();
        return this.active;
    }

    /** 清空所有测量点 */
    clear() {
        this.points.length = 0;
        this.selection = -1;
        this.gizmo?.detach();
        this.app.renderNextFrame = true;
    }

    /** 删除当前选中点 */
    deleteSelected() {
        if (this.selection < 0 || this.selection >= this.points.length) return;
        this.points.splice(this.selection, 1);
        // 选中索引向后回退一位
        if (this.points.length === 0) {
            this.selection = -1;
        } else if (this.selection >= this.points.length) {
            this.selection = this.points.length - 1;
        }
        this.attachGizmoToSelection();
        this.app.renderNextFrame = true;
    }

    isActive() {
        return this.active;
    }

    setUnitSystem(system: 'metric' | 'imperial') {
        this.unitSystem = system;
        this.app.renderNextFrame = true;
    }

    destroy() {
        this.deactivate();
        this.app.off('postrender', this.onPostrender);
        this.svg.remove();
        window.removeEventListener('resize', this.onResize);
        if (this.gizmo) {
            this.gizmo.destroy();
            this.gizmo = null;
        }
        if (this.gizmoPivot) {
            this.gizmoPivot.destroy();
            this.gizmoPivot = null;
        }
        if (this.gizmoLayer) {
            this.app.scene.layers.remove(this.gizmoLayer);
            this.gizmoLayer = null;
        }
    }

    // === gizmo / selection 相关 ===

    // 设置选中点索引并把 gizmo 吸附到该点
    private setSelection(index: number) {
        this.selection = index;
        this.attachGizmoToSelection();
        this.app.renderNextFrame = true;
    }

    /** 懒初始化 gizmo 与其占位 entity */
    private ensureGizmo() {
        if (this.gizmo) return;
        const cameraComp = this.camera.camera!;
        // 复用现有 Gizmo 层（若由其他工具/Annotations 已创建），否则创建一个
        const existing = this.app.scene.layers.getLayerByName('Gizmo');
        this.gizmoLayer = existing ?? Gizmo.createLayer(this.app);
        if (existing && !cameraComp.layers.includes(this.gizmoLayer.id)) {
            cameraComp.layers = [...cameraComp.layers, this.gizmoLayer.id];
        }

        this.gizmoPivot = new Entity('measureGizmoPivot');
        this.app.root.addChild(this.gizmoPivot);

        this.gizmo = new TranslateGizmo(cameraComp, this.gizmoLayer);
        this.updateGizmoSize();
        // 画布尺寸变化时同步调整 gizmo 大小
        window.addEventListener('resize', this.onResize);

        // gizmo 渲染时主动请求一帧
        this.gizmo.on('render:update', () => {
            this.app.renderNextFrame = true;
        });
        this.gizmo.on('transform:start', () => {
            this.gizmoBusy = true;
            this.clicked = false;
            // 通知 InputController 在 gizmo 拖拽期间丢弃相机输入增量，
            // 避免同一次指针拖拽同时被 gizmo 和相机响应
            this.events?.fire('measure.gizmoBusy', true);
        });
        this.gizmo.on('transform:move', () => {
            if (this.selection < 0 || this.selection >= this.points.length) return;
            // 读取 pivot 的世界坐标写回测量点
            this.points[this.selection].copy(this.gizmoPivot!.getPosition());
            this.app.renderNextFrame = true;
        });
        this.gizmo.on('transform:end', () => {
            this.gizmoBusy = false;
            this.events?.fire('measure.gizmoBusy', false);
        });
    }

    private onResize = () => {
        this.updateGizmoSize();
    };

    // 根据画布尺寸调整 gizmo 显示大小（与 manifold_supersplat 一致）
    private updateGizmoSize() {
        if (!this.gizmo) return;
        const w = this.canvas.clientWidth;
        const h = this.canvas.clientHeight;
        const m = Math.max(w, h);
        if (m > 0) {
            // 移动端（粗指针）放大 gizmo 便于点选
            const isCoarse = typeof window !== 'undefined' &&
                window.matchMedia?.('(pointer: coarse)').matches;
            this.gizmo.size = (isCoarse ? 1000 : 1200) / m;
        }
    }

    /** 将 gizmo 吸附到当前选中的点，若没有选中点则 detach */
    private attachGizmoToSelection() {
        const g = this.gizmo;
        const pivot = this.gizmoPivot;
        if (!g || !pivot) return;
        if (this.selection < 0 || this.selection >= this.points.length) {
            g.detach();
            return;
        }
        pivot.setPosition(this.points[this.selection]);
        g.attach(pivot);
    }
}

export { MeasureTool };
